import React, { createContext, useContext, useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PWAContextType {
  canInstall: boolean;
  isStandalone: boolean;
  installApp: () => Promise<void>;
}

const PWAContext = createContext<PWAContextType>({
  canInstall: false,
  isStandalone: false,
  installApp: async () => {}
});

export const PWAProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if app is already installed or running as PWA
    const checkStandalone = () => {
      const isInStandaloneMode = 
        window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone === true ||
        document.referrer.includes('android-app://') ||
        window.location.href.includes('?mode=pwa') ||
        localStorage.getItem('pwaInstalled') === 'true';
      
      setIsStandalone(isInStandaloneMode);
      setCanInstall(!isInStandaloneMode);
      
      // If running as PWA, add query param to all internal links
      if (isInStandaloneMode) {
        document.querySelectorAll('a[href^="/"]').forEach(link => {
          const href = link.getAttribute('href');
          if (href && !href.includes('?mode=pwa')) {
            link.setAttribute('href', `${href}${href.includes('?') ? '&' : '?'}mode=pwa`);
          }
        });
      }
    };

    checkStandalone();

    // Listen for display mode changes
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleDisplayChange = (e: MediaQueryListEvent) => {
      const isInStandaloneMode = e.matches || localStorage.getItem('pwaInstalled') === 'true';
      setIsStandalone(isInStandaloneMode);
      setCanInstall(!isInStandaloneMode);
    };
    mediaQuery.addListener(handleDisplayChange);

    // Handle install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      if (!isStandalone) {
        e.preventDefault();
        setDeferredPrompt(e as BeforeInstallPromptEvent);
        setCanInstall(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Handle successful installation
    const handleAppInstalled = () => {
      localStorage.setItem('pwaInstalled', 'true');
      setDeferredPrompt(null);
      setCanInstall(false);
      setIsStandalone(true);
      
      // Add PWA mode to current URL if not present
      if (!window.location.href.includes('mode=pwa')) {
        const newUrl = `${window.location.href}${window.location.href.includes('?') ? '&' : '?'}mode=pwa`;
        window.history.replaceState({}, '', newUrl);
      }
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    // Check URL parameters for PWA mode
    if (window.location.href.includes('mode=pwa')) {
      setIsStandalone(true);
      setCanInstall(false);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      mediaQuery.removeListener(handleDisplayChange);
    };
  }, [isStandalone]);

  const installApp = async () => {
    if (!deferredPrompt) {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
      if (isIOS) {
        alert('Um die App zu installieren:\n1. Tippen Sie auf das "Teilen"-Symbol\n2. Wählen Sie "Zum Home-Bildschirm hinzufügen"');
        localStorage.setItem('pwaInstalled', 'true');
        setCanInstall(false);
        setIsStandalone(true);
      }
      return;
    }

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        localStorage.setItem('pwaInstalled', 'true');
        setDeferredPrompt(null);
        setCanInstall(false);
        setIsStandalone(true);
      }
    } catch (error) {
      console.error('Error installing app:', error);
    }
  };

  return (
    <PWAContext.Provider value={{ canInstall, isStandalone, installApp }}>
      {children}
    </PWAContext.Provider>
  );
};

export const usePWA = () => useContext(PWAContext);